import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { t } from '@/i18n'
import { getCounterparties } from '@/lib/api'
import { formatDateTime, formatMoney } from '@/lib/format'

const LEVEL_KEY: Record<string, string> = {
  low: 'risk.low',
  medium: 'risk.medium',
  high: 'risk.high',
  critical: 'risk.critical',
}

function riskLabel(level: string | null | undefined): string {
  const key = LEVEL_KEY[level ?? 'low']
  return key ? t(key) : (level ?? '')
}

export function Counterparties() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['counterparties'],
    queryFn: getCounterparties,
  })

  return (
    <div className="mx-auto max-w-5xl px-4 py-5 md:px-8 md:py-8">
      <h1 className="text-xl font-semibold text-slate-100">{t('cp.title')}</h1>
      <p className="mt-1 text-sm text-slate-500">{t('cp.lead')}</p>
      {isLoading && <p className="mt-6 text-sm text-slate-500">{t('common.loading')}</p>}
      {isError && <p className="mt-6 text-sm text-loss">{error instanceof Error ? error.message : t('common.error')}</p>}
      {data && data.length === 0 && <p className="mt-6 text-sm text-slate-500">{t('cp.empty')}</p>}
      {data && data.length > 0 && (
        <table className="mt-6 w-full text-sm">
          <thead className="text-left text-xs text-slate-500">
            <tr>
              <th className="py-2">{t('cp.nick')}</th>
              <th className="py-2">{t('common.exchange')}</th>
              <th className="py-2 text-right">{t('cp.deals')}</th>
              <th className="py-2 text-right">{t('cp.volume')}</th>
              <th className="py-2">{t('cp.indicator')}</th>
              <th className="py-2">{t('cp.last')}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item) => (
              <tr key={item.id} className="border-t border-surface-border/60">
                <td className="py-2">
                  <Link to={`/counterparties/${item.id}`} className="text-slate-200 hover:text-white">
                    {item.nickname ?? item.display_label ?? '—'}
                    {item.is_watchlisted ? <span className="ml-2 text-xs text-pending">{t('cp.watch')}</span> : null}
                  </Link>
                </td>
                <td className="py-2 text-slate-400">{item.exchange_code}</td>
                <td className="num py-2 text-right">{item.orders_total}</td>
                <td className="num py-2 text-right">{formatMoney(item.volume_base)}</td>
                <td className="py-2 text-slate-300">
                  {riskLabel(item.risk_level)}
                  {item.risk_score != null ? <span className="num text-slate-500"> · {item.risk_score}</span> : null}
                </td>
                <td className="py-2 text-slate-400">
                  {item.last_seen_at ? formatDateTime(item.last_seen_at) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
